import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';

import { getMarketTradingCalendarApi } from '@flipster/apis/v2/market';
import { deleteMultiOpenPositions, postOneWayCloseOrderApi } from '@flipster/apis/v2/trade';
import { accountBunja } from '@flipster/r230522/original/account';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';

import { orderAttributionAtom } from '../../order-attribution';
import { snapshotSpecBunja } from '../../snapshot/state';
import { isSymbolTradable } from '../../trading-suspension/state';
import { buildRequestStamp } from '../base-params';
import {
  classifyCloseAllOutcome,
  sortByQuantityAsc,
  splitByTradability,
  toCloseOrderFields,
  type CloseAllOutcome,
  type CloseAllPositionInput,
} from './utils';

interface CloseAllVariables<P extends CloseAllPositionInput> {
  positions: P[];
  /** copy-trading 서브계정. 생략 시 메인 계정. */
  accountId?: number;
}

/**
 * Close-all 실행 액션 (구 apps/web useClosePositions의 실행 부분).
 * tradable/suspended 분리 → tradeMode 분기 API → 결과 분류(CloseAllOutcome)까지 담당한다.
 * 페이로드 비대칭 보존: Multi 경로는 attribution을 보내지 않고, OneWay만 attribution+airdropPositions:[]를 보낸다.
 * confirm alert/알림 모달/성공 토스트 등 UI 반응은 호출측(apps/web useClosePositions)이 outcome으로 처리한다.
 * 에러: 스낵바 표시 후 null 반환(기존과 동일하게 rethrow 없음).
 */
export const closeAllPositionsActionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const { v2AccountAtom } = bunja.use(accountBunja);

  const tradingCalendarQueryAtom = atomWithQuery(() => ({
    queryKey: ['getMarketTradingCalendarApi'],
    queryFn: () => getMarketTradingCalendarApi(),
    select: (res: Awaited<ReturnType<typeof getMarketTradingCalendarApi>>) => res.data,
    staleTime: 60 * 60 * 1000,
  }));

  /** RWA 거래시간 판정 predicate — close-all 외에도 suspended 노티스 판단(호출측)에 쓰인다. */
  const isSymbolCurrentlyTradableAtom = atom((get) => {
    const instruments = get(instrumentsKeyBySymbolAtom);
    const calendarData = get(tradingCalendarQueryAtom).data;
    return (symbol: string): boolean => isSymbolTradable(instruments?.[symbol], calendarData);
  });

  const closeMultipleMutationAtom = atomWithMutation(() => ({
    mutationKey: ['deleteMultiOpenPositions'],
    mutationFn: deleteMultiOpenPositions,
  }));
  const closeOneWayMutationAtom = atomWithMutation(() => ({
    mutationKey: ['postOneWayCloseOrderApi'],
    mutationFn: postOneWayCloseOrderApi,
  }));

  const isCloseAllLoadingAtom = atom(
    (get) => get(closeMultipleMutationAtom).isPending || get(closeOneWayMutationAtom).isPending,
  );

  const closeAllPositions = async <P extends CloseAllPositionInput>({
    positions,
    accountId,
  }: CloseAllVariables<P>): Promise<CloseAllOutcome<P> | null> => {
    try {
      const account = store.get(v2AccountAtom);
      if (!account) throw new Error('Unexpected missing account');

      const isTradable = store.get(isSymbolCurrentlyTradableAtom);
      const { tradablePositions, suspendedPositions } = splitByTradability(positions, isTradable);
      const orderedTradable = sortByQuantityAsc(tradablePositions);
      const stamp = buildRequestStamp();

      const res = await (() => {
        switch (account.tradeMode) {
          case 'MultiplePositions':
            return store.get(closeMultipleMutationAtom).mutateAsync({
              ...stamp,
              positions: orderedTradable.map((p) => ({ ...toCloseOrderFields(p), slot: p.slot })),
              accountId,
            });
          case 'OneWay':
            return store.get(closeOneWayMutationAtom).mutateAsync({
              ...stamp,
              positions: orderedTradable.map(toCloseOrderFields),
              airdropPositions: [],
              attribution: store.get(orderAttributionAtom),
              accountId,
            });
          default:
            account.tradeMode satisfies never;
            throw new Error('Unexpected trade mode');
        }
      })();

      return classifyCloseAllOutcome(res.data.positions, tradablePositions, suspendedPositions);
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
      return null;
    }
  };

  return { closeAllPositions, isSymbolCurrentlyTradableAtom, isCloseAllLoadingAtom };
});
closeAllPositionsActionBunja.debugLabel = 'closeAllPositionsActionBunja';
